import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './VerifyEmailPage.css';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

export default function VerifyEmailPage() {
  const navigate  = useNavigate();
  const location  = useLocation();

  // Email passed from register page via navigation state
  const emailFromState = (location.state as any)?.email || '';

  const [email,   setEmail]   = useState(emailFromState);
  const [code,    setCode]    = useState(['', '', '', '', '', '']);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first empty input
  useEffect(() => { inputs.current[0]?.focus(); }, []);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    // Auto-advance
    if (digit && index < 5) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) { setError('Please enter all 6 digits.'); return; }
    if (!email.includes('@'))  { setError('Please enter your email address.'); return; }

    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: fullCode }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message);
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setError(data.error || 'Verification failed.');
        setCode(['', '', '', '', '', '']);
        inputs.current[0]?.focus();
      }
    } catch {
      setError('Could not connect to the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="verify-page">
      <div className="verify-card">
        <a href="/login">
          <img src="/guardian-bazaar-logo-dark.png" alt="Guardian Bazaar" className="verify-logo" />
        </a>

        {success ? (
          <>
            <div className="verify-icon success">✓</div>
            <h1 className="verify-title">Email verified!</h1>
            <p className="verify-subtitle">{success}</p>
            <p className="verify-redirect">Redirecting to sign in…</p>
          </>
        ) : (
          <>
            <h1 className="verify-title">Verify your email</h1>
            <p className="verify-subtitle">
              We sent a 6-digit code to <strong>{email || 'your email address'}</strong>.<br/>
              Enter it below to activate your account.
            </p>

            <form className="verify-form" onSubmit={handleSubmit}>
              {!emailFromState && (
                <div className="verify-email-field">
                  <label>Email Address</label>
                  <input type="email" value={email}
                         onChange={e => setEmail(e.target.value)}
                         placeholder="your@email.com" required />
                </div>
              )}

              <div className="verify-code-row" onPaste={handlePaste}>
                {code.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => { inputs.current[i] = el; }}
                    type="text" inputMode="numeric"
                    maxLength={1} value={digit}
                    onChange={e => handleCodeChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    className="verify-code-input"
                  />
                ))}
              </div>

              {error && <div className="verify-error">{error}</div>}

              <button type="submit" className="verify-btn" disabled={loading}>
                {loading ? 'Verifying…' : 'Verify Account'}
              </button>
            </form>

            <p className="verify-note">
              Didn't receive it? Check your spam folder.<br/>
              Code expires in 24 hours.
            </p>
          </>
        )}

        <a href="/login" className="verify-back">← Back to sign in</a>
      </div>
    </div>
  );
}
